import { useState } from "react";
import { Lock, Mail, Eye, EyeOff, Loader2, ArrowLeft } from "lucide-react";
import { useAuth } from "../hooks/useAuth";

type Mode = "signin" | "signup" | "reset";

const GOOGLE_OAUTH_ENABLED = import.meta.env.VITE_ENABLE_GOOGLE_OAUTH === "true";

export default function LoginView() {
  const { signIn, signUp, requestPasswordReset, resendSignupConfirmation, signInWithGoogle } = useAuth();
  const [mode, setMode] = useState<Mode>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [signedUp, setSignedUp] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const handleGoogle = async () => {
    setError(null);
    setGoogleLoading(true);
    const err = await signInWithGoogle();
    // On success the browser redirects to Google, so we only reach here on error.
    if (err) { setError(err.message); setGoogleLoading(false); }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      const err = await resendSignupConfirmation(email);
      if (err) setError(err.message);
      else setResent(true);
    } finally {
      setResending(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      if (mode === "signin") {
        const err = await signIn(email, password);
        if (err) setError(err.message);
      } else if (mode === "reset") {
        const err = await requestPasswordReset(email);
        if (err) setError(err.message);
        else setResetSent(true);
      } else {
        const err = await signUp(email, password);
        if (err) {
          setError(err.message);
        } else {
          setSignedUp(true);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full bg-rc-paper border-[1.5px] border-rc-fog rounded-xl px-4 py-3 text-[15px] text-rc-black placeholder:text-rc-slate/70 focus:outline-none focus:border-rc-berry focus:ring-2 focus:ring-rc-berry/15 transition-colors ease-rc-out";

  if (signedUp) {
    return (
      <Shell>
        <div className="w-full max-w-sm text-center animate-fade-up">
          <div className="w-12 h-12 rounded-full bg-rc-berry/10 border border-rc-berry/30 flex items-center justify-center mx-auto mb-7">
            <Mail className="w-5 h-5 text-rc-berry" />
          </div>
          <h1 className="text-[28px] font-bold text-rc-black tracking-[-0.02em] leading-[1.15] mb-3">
            Bestätigung im Postfach.
          </h1>
          <p className="text-rc-charcoal text-[15px] leading-[1.6]">
            Wir haben einen Bestätigungslink an{" "}
            <strong className="font-semibold text-rc-black">{email}</strong> gesendet.
            Bestätige die E-Mail, um dein Konto zu aktivieren.
          </p>
          <p className="text-rc-slate text-[13px] leading-[1.55] mt-4">
            Keine E-Mail nach ein paar Minuten? Prüfe deinen <strong className="font-semibold text-rc-charcoal">Spam-</strong> oder{" "}
            <strong className="font-semibold text-rc-charcoal">Quarantäne-Ordner</strong> — bei Microsoft 365 oft unter „Quarantäne", nicht im Junk-Ordner.
          </p>

          {error && (
            <div className="mt-4 bg-rc-danger/8 border border-rc-danger/30 rounded-xl px-4 py-3 text-rc-danger text-[13px] font-medium">
              {error}
            </div>
          )}

          {resent ? (
            <p className="mt-6 text-rc-charcoal text-[14px] font-medium">Bestätigung erneut gesendet ✓</p>
          ) : (
            <button
              type="button"
              onClick={handleResend}
              disabled={resending}
              className="mt-6 inline-flex items-center justify-center gap-2 border border-rc-fog hover:border-rc-charcoal/30 text-rc-charcoal hover:text-rc-black rounded-pill px-5 py-2.5 text-[14px] font-semibold transition-colors disabled:opacity-50"
            >
              {resending && <Loader2 className="w-4 h-4 animate-spin" />}
              E-Mail erneut senden
            </button>
          )}

          <div>
            <button
              type="button"
              onClick={() => { setSignedUp(false); setResent(false); setError(null); setMode("signin"); }}
              className="mt-7 text-rc-berry hover:text-rc-berry-700 text-[14px] font-semibold transition-colors ease-rc-out"
            >
              Zurück zur Anmeldung
            </button>
          </div>
        </div>
      </Shell>
    );
  }

  if (resetSent) {
    return (
      <Shell>
        <div className="w-full max-w-sm text-center animate-fade-up">
          <div className="w-12 h-12 rounded-full bg-rc-berry/10 border border-rc-berry/30 flex items-center justify-center mx-auto mb-7">
            <Mail className="w-5 h-5 text-rc-berry" />
          </div>
          <h1 className="text-[28px] font-bold text-rc-black tracking-[-0.02em] leading-[1.15] mb-3">
            Prüfe dein Postfach.
          </h1>
          <p className="text-rc-charcoal text-[15px] leading-[1.6]">
            Falls ein Konto zu{" "}
            <strong className="font-semibold text-rc-black">{email}</strong> existiert,
            haben wir dir einen Link zum Zurücksetzen deines Passworts gesendet.
          </p>
          <button
            type="button"
            onClick={() => { setResetSent(false); setMode("signin"); }}
            className="mt-7 text-rc-berry hover:text-rc-berry-700 text-[14px] font-semibold transition-colors ease-rc-out"
          >
            Zurück zur Anmeldung
          </button>
        </div>
      </Shell>
    );
  }

  const isSignin = mode === "signin";
  const isReset = mode === "reset";

  return (
    <Shell>
      <div className="w-full max-w-sm animate-fade-up">
        <div className="flex flex-col items-center mb-8">
          <a
            href="/"
            aria-label="Zur Startseite"
            className="inline-flex items-center gap-2.5 mb-7 group"
          >
            <ArrowLeft className="w-3.5 h-3.5 text-rc-slate group-hover:text-rc-black transition-colors" />
            <img
              src="/brand/readycal-logo.png"
              alt="READYCAL"
              className="h-5 w-auto block opacity-90 group-hover:opacity-100 transition-opacity"
            />
          </a>
          <h1 className="text-[28px] font-bold text-rc-black tracking-[-0.02em] leading-[1.1] text-center">
            {isReset ? "Passwort zurücksetzen." : isSignin ? "Facilitator-Login." : "Konto erstellen."}
          </h1>
          <p className="text-rc-charcoal text-[15px] mt-2.5 text-center max-w-[22rem] leading-[1.55]">
            {isReset
              ? <>Gib deine E-Mail ein — wir senden dir einen <strong className="font-semibold text-rc-black">Link zum Zurücksetzen</strong>.</>
              : isSignin
              ? <>Melde dich an, um <strong className="font-semibold text-rc-black">deine Events</strong> zu steuern.</>
              : <>Starte deine <strong className="font-semibold text-rc-black">Echtzeit-Insight-Sessions</strong>.</>}
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-rc-paper border border-rc-fog rounded-rc-lg p-7 shadow-cardLg flex flex-col gap-3.5"
        >
          {error && (
            <div className="bg-rc-danger/8 border border-rc-danger/30 rounded-xl px-4 py-3 text-rc-danger text-[14px] font-medium">
              {error}
            </div>
          )}

          <Field label="E-Mail" htmlFor="email">
            <div className="relative">
              <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-rc-slate pointer-events-none" />
              <input
                id="email"
                type="email"
                placeholder="du@beispiel.de"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className={`${inputClass} pl-10`}
              />
            </div>
          </Field>

          {!isReset && (
            <Field label="Passwort" htmlFor="password">
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-rc-slate pointer-events-none" />
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete={isSignin ? "current-password" : "new-password"}
                  className={`${inputClass} pl-10 pr-11`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Passwort verbergen" : "Passwort anzeigen"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-rc-slate hover:text-rc-black rounded-full transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </Field>
          )}

          <button
            type="submit"
            disabled={loading}
            className="mt-2 inline-flex items-center justify-center gap-2 bg-rc-berry hover:bg-rc-berry-700 disabled:bg-rc-fog disabled:text-rc-slate disabled:cursor-not-allowed text-rc-paper rounded-pill px-6 py-3.5 text-[15px] font-semibold active:scale-[0.98] transition ease-rc-out"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {isReset ? "Link senden" : isSignin ? "Anmelden" : "Konto erstellen"}
          </button>

          {isSignin && (
            <button
              type="button"
              onClick={() => { setMode("reset"); setError(null); }}
              className="mt-1 text-center text-rc-charcoal hover:text-rc-black text-[13px] font-medium transition-colors"
            >
              Passwort vergessen?
            </button>
          )}

          {!isReset && GOOGLE_OAUTH_ENABLED && (
            <>
              <div className="flex items-center gap-3 mt-1">
                <span className="h-px flex-1 bg-rc-fog" />
                <span className="text-[12px] text-rc-slate font-medium">oder</span>
                <span className="h-px flex-1 bg-rc-fog" />
              </div>
              <button
                type="button"
                onClick={handleGoogle}
                disabled={googleLoading}
                className="inline-flex items-center justify-center gap-2.5 border border-rc-fog hover:border-rc-charcoal/30 bg-rc-paper text-rc-black rounded-pill px-6 py-3 text-[15px] font-semibold transition-colors disabled:opacity-50 active:scale-[0.98]"
              >
                {googleLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <GoogleIcon />}
                Mit Google anmelden
              </button>
            </>
          )}
        </form>

        <p className="mt-5 text-center text-rc-charcoal text-[14px]">
          {isReset ? (
            <button
              type="button"
              onClick={() => { setMode("signin"); setError(null); }}
              className="text-rc-berry hover:text-rc-berry-700 font-semibold transition-colors"
            >
              Zurück zur Anmeldung
            </button>
          ) : (
            <>
              {isSignin ? "Noch kein Konto? " : "Schon angemeldet? "}
              <button
                type="button"
                onClick={() => { setMode(isSignin ? "signup" : "signin"); setError(null); }}
                className="text-rc-berry hover:text-rc-berry-700 font-semibold transition-colors"
              >
                {isSignin ? "Konto erstellen" : "Zur Anmeldung"}
              </button>
            </>
          )}
        </p>
      </div>
    </Shell>
  );
}

// Google's brand mark is not in lucide-react; inline the official multicolor G.
function GoogleIcon() {
  return (
    <svg className="w-[18px] h-[18px]" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92a8.78 8.78 0 0 0 2.68-6.62z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.02-3.7H.96v2.34A9 9 0 0 0 9 18z" />
      <path fill="#FBBC05" d="M3.98 10.72a5.4 5.4 0 0 1 0-3.44V4.94H.96a9 9 0 0 0 0 8.12l3.02-2.34z" />
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.46 3.44 1.35l2.58-2.58C13.47.9 11.43 0 9 0A9 9 0 0 0 .96 4.94l3.02 2.34C4.68 5.16 6.66 3.58 9 3.58z" />
    </svg>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-rc-cream text-rc-black flex flex-col items-center justify-center px-6 py-16 font-sans antialiased">
      {children}
    </div>
  );
}

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label
        htmlFor={htmlFor}
        className="text-[11px] font-semibold uppercase tracking-[0.14em] text-rc-charcoal block mb-2"
      >
        {label}
      </label>
      {children}
    </div>
  );
}
